import {
  TicketPriority,
  TicketSlaState,
  TicketSlaTargetState
} from '../../shared/models/ticket.model';
import { TicketSlaTargetUiModel, TicketUiModel } from '../../shared/ui-models/ticket.ui-model';

// Same chip as the leads screens. Copied, not imported: modules don't import each other.
export const CHIP =
  'inline-flex items-center rounded-full px-[10px] py-[3px] text-[12px] font-gilroy-medium whitespace-nowrap';

/**
 * Coral for P1 only: it is the "drop what you are doing" colour across the admin,
 * and spending it on P2 would teach people to ignore it.
 */
export const PRIORITY_STYLE: Record<TicketPriority, string> = {
  P1: 'bg-secondary-100 text-secondary-800',
  P2: 'bg-creams-600 text-creams-900',
  P3: 'bg-blue text-neutral-500',
  P4: 'bg-neutral-50 text-neutral-400'
};

export const PRIORITY_LABEL: Record<TicketPriority, string> = {
  P1: 'P1 Critical',
  P2: 'P2 Urgent',
  P3: 'P3 Standard',
  P4: 'P4 Low'
};

export const stateStyle = (ticket: TicketUiModel): string =>
  ticket.isClosed
    ? 'bg-neutral-50 text-neutral-300'
    : ticket.state === 'new'
    ? 'bg-primary-50 text-primary-700'
    : 'bg-creams-500 text-creams-900';

/** Same rule as priority: coral only for what is already late. */
export const SLA_STYLE: Record<TicketSlaState, string> = {
  breached: 'bg-secondary-500 text-white',
  at_risk: 'bg-creams-600 text-creams-900',
  on_track: 'bg-primary-50 text-primary-700',
  met: 'bg-neutral-50 text-neutral-400',
  none: 'bg-neutral-50 text-neutral-300'
};

export const SLA_LABEL: Record<TicketSlaState, string> = {
  breached: 'Breached',
  at_risk: 'Due soon',
  on_track: 'On track',
  met: 'Met',
  none: 'No SLA'
};

export const TARGET_STYLE: Record<TicketSlaTargetState, string> = {
  overdue: 'bg-secondary-500 text-white',
  missed: 'bg-secondary-100 text-secondary-800',
  due_soon: 'bg-creams-600 text-creams-900',
  on_track: 'bg-primary-50 text-primary-700',
  met: 'bg-neutral-50 text-neutral-400',
  none: 'bg-neutral-50 text-neutral-300'
};

export const TARGET_LABEL: Record<TicketSlaTargetState, string> = {
  overdue: 'Overdue',
  missed: 'Missed',
  due_soon: 'Due soon',
  on_track: 'On track',
  met: 'Met',
  none: 'No target'
};

/** "3h 20m", "45m", "2d 4h": the larger two units of a duration. */
export function duration(ms: number): string {
  const m = Math.max(0, Math.round(Math.abs(ms) / 60_000));
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const min = m % 60;
  if (d) return h ? `${d}d ${h}h` : `${d}d`;
  if (h) return min ? `${h}h ${min}m` : `${h}h`;
  return `${min}m`;
}

/** How far a due time is from now, as the list reads it: "in 45m" / "2h 10m late". */
export function dueIn(due: Date | undefined, now = Date.now()): string | undefined {
  if (!due) return undefined;
  const ms = due.getTime() - now;
  return ms >= 0 ? `in ${duration(ms)}` : `${duration(ms)} late`;
}

/** One target's line in the SLA panel: when it was due, and when (or whether) it was done. */
export function targetDetail(t: TicketSlaTargetUiModel, now = Date.now()): string {
  if (t.state === 'none') return 'No target for this ticket';
  const due = t.due ? t.due.toLocaleString() : 'not set';
  if (t.at) {
    const late =
      t.due && t.at > t.due ? `, ${duration(t.at.getTime() - t.due.getTime())} late` : '';
    return `Due ${due} · done ${t.at.toLocaleString()}${late}`;
  }
  return `Due ${due} (${dueIn(t.due, now)})`;
}

export const titleCase = (value: string): string =>
  (value || '').replace(/_/g, ' ').replace(/^./, (c: string) => c.toUpperCase());
